import { storeToRefs } from 'pinia'
import {
  KeyTravelFamily,
  type GcomeKeyTravel,
  type KeyTravelList,
  type KeyTravelWriteList,
} from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'

/** 行程页可编辑字段。轴体类型 / SOCD 不在此写入，由 SDK 读-改-写保留。 */
const toItineraryWritePatch = (keyTravel: GcomeKeyTravel): Partial<GcomeKeyTravel> => ({
  triggerType: keyTravel.triggerType,
  travel: keyTravel.travel,
  precisionBits: keyTravel.precisionBits,
  pressRt: keyTravel.pressRt,
  pressDeadzone: keyTravel.pressDeadzone,
  releaseRt: keyTravel.releaseRt,
  releaseDeadzone: keyTravel.releaseDeadzone,
})

/** GCOME 触发行程 SDK 封装：演示模式开关、全键读取、选中键补丁写入。 */
export const useItinerarySDK = () => {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const getKeyTravelFeature = () => {
    const feature = currentDevice.value?.features.keyTravel
    if (!feature) {
      console.warn('Current device does not support key travel')
      return undefined
    }

    return feature
  }

  const switchDemoMode = async (state: boolean) => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      await feature.switchDemoMode(state)
    } catch (error) {
      console.error('Failed to switch key travel demo mode', error)
      defaultReportError(`Failed to switch key travel demo mode: ${JSON.stringify(error)}`)
      throw error
    }
  }

  /** GCOME_AP 无 RT 档位，传入 0 读取当前所有键位行程。 */
  const getKeyItineraryList = async (): Promise<KeyTravelList | undefined> => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      return await feature.getKeyTravel(0)
    } catch (error) {
      console.error('Failed to get key itinerary list', error)
      defaultReportError(`Failed to get key itinerary list: ${JSON.stringify(error)}`)
      throw error
    }
  }

  /** GCOME_AP 写入支持行程字段补丁，SDK 会保留未传字段。 */
  const setKeyItineraryList = async (payload: KeyTravelWriteList) => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      return await feature.setKeyTravel(payload)
    } catch (error) {
      console.error('Failed to set key itinerary list', error)
      defaultReportError(`Failed to set key itinerary list: ${JSON.stringify(error)}`)
      throw error
    }
  }

  /** 将选中键的行程 UI 状态写成补丁并一次性下发（整块读-改-写）。 */
  const setSelectedKeyItinerary = async (
    entries: Array<{ index: number; keyTravel: GcomeKeyTravel }>,
  ) => {
    if (!entries.length) return
    const payload: KeyTravelWriteList = entries.map(({ index, keyTravel }) => ({
      family: KeyTravelFamily.Gcome,
      index,
      keyTravel: toItineraryWritePatch(keyTravel),
    }))
    return setKeyItineraryList(payload)
  }

  return {
    getKeyTravelFeature,
    switchDemoMode,
    getKeyItineraryList,
    setKeyItineraryList,
    setSelectedKeyItinerary,
  }
}
